import {
    Row,
    Col,
    Typography,
    Table,
    Button,
    message,
    Descriptions,
  } from "antd";
  import { routes } from "common/routes";
  import { generateHeaders, getSlotName } from "common/utils";
  import { AppPageHeader } from "components/AppPageHeader";
  import { useAppDispatch } from "hooks/useAppDispatch";
  import { useAppSelector } from "hooks/useAppSelector";
  import { useDocumentTitle } from "hooks/usePageTitle";
  import { useEffect, useMemo } from "react";
  import { useNavigate } from "react-router-dom";
import { removeProcessingClusterStoreMapping, saveClusterStoreMapping, cancelClusterStoreMapping } from "store/cluster-slice/clusterServicesSlice";
  
  const MESSAGE_KEY = "SAVE_MIDDLE_MILE_MAPPING";
  
  export const SaveClusterStoreMappingPage = () => {
    useDocumentTitle("Cluster Store Mapping: Save Mapping");
    const { processingClusterStoreMapping, status, error } = useAppSelector(
      (state) => state.clusterServices
    );
    const { slots } = useAppSelector((state) => state.requisition);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
  
    useEffect(() => {
      const { timeUploaded } = processingClusterStoreMapping;
      if (timeUploaded) {
        const uploadedTime = new Date(timeUploaded).getTime();
        const currentTime = new Date().getTime();
        const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
  
        if (minsPassed >= 15) {
          dispatch(removeProcessingClusterStoreMapping());
        }
      }
  
      if (!Object.keys(processingClusterStoreMapping).length) {
        navigate(`/${routes.CLUSTER_SERVICE.UPLOAD_CLUSTER_STORE_MAPPING}`);
      }
    }, [dispatch, processingClusterStoreMapping, navigate]);
  
    useEffect(() => {
      if (status === "loading") {
        message.loading({ content: "Saving Cluster Store mapping", key: MESSAGE_KEY });
      } else if (status === "success") {
        message.success({
          content: "Cluster Store mapping saved!",
          duration: 5,
          key: MESSAGE_KEY,
        });
      } else if (status === "cancelling") {
        message.loading({ content: "Cancelling Cluster Store mapping", key: MESSAGE_KEY });
      } else if (status === "error") {
        message.error({ content: error, key: MESSAGE_KEY });
      }
  
      return () => {
        message.destroy();
      };
    }, [status, error]);
  
    let content;
    const { headerMapping } = processingClusterStoreMapping;
    const { Text } = Typography;
  
    const generateMemoizedHeaders = useMemo(
      () => generateHeaders(headerMapping),
      [headerMapping]
    );
  
    const onSaveUserMapping = () => {
      dispatch(saveClusterStoreMapping());
    };
  
    const onCancelSave = () => {
      dispatch(cancelClusterStoreMapping());
    };
  
    if (!Object.keys(processingClusterStoreMapping).length) {
      content = <Text>Nothing to show here!</Text>;
    } else {
      const headers = generateMemoizedHeaders || {};
      const successRows = processingClusterStoreMapping.successRows;
      const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
      const columns = [
        ...Object.keys(headers).map(
          (key) =>
            ({
              key,
              title: headers[key],
              dataIndex: key,
            } as any)
        ),
      ];
      content = (
        <Table
          size="small"
          columns={columns}
          dataSource={data}
          pagination={false}
          scroll={{ y: 500 }}
          summary={() => (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Row>
                    <Col>
                      <Button
                        type="primary"
                        onClick={onSaveUserMapping}
                        loading={status === "loading"}
                        disabled={status === "loading"}
                      >
                        Save Mapping
                      </Button>
                    </Col>
                    <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                      <Button
                        onClick={onCancelSave}
                        disabled={status === "loading"}
                      >
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        ></Table>
      );
    }
  
    return (
      <>
        <Row>
          <Col>
            <AppPageHeader title="Save Cluster Store Mapping" />
          </Col>
        </Row>
        <Row style={{ marginBottom: "1rem" }}>
          {!!(slots && processingClusterStoreMapping.slot) && (
            <Descriptions
              size="small"
              layout="vertical"
              labelStyle={{ fontWeight: "bold" }}
              bordered
              column={{ xs: 2 }}
            >
              <Descriptions.Item label="Date">
                {processingClusterStoreMapping.successRows?.at(0)?.deliveryDate}
              </Descriptions.Item>
              <Descriptions.Item label="Slot">
                {getSlotName(slots, processingClusterStoreMapping.slot)}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Row>
        <Row>
          <Col xs={24}>{content}</Col>
        </Row>
      </>
    );
  };
  